import React, { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DraggableSyntheticListeners,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isArray } from 'lodash';
import { nanoid } from 'nanoid';
import { ItemContainer, ReorderContainer } from './styles';
import Icon from '../Icon';

type ReorderItemType = {
  id: string;
};

type ReorderProps<T extends ReorderItemType = any> = {
  items: T[];
  className?: string;
  disabled?: boolean;
  showDragger?: boolean;
  customFilter?: (item: T) => boolean;
  onReorder?: (items: T[]) => void;
  customRender: (
    item: T,
    index: number,
    listeners?: DraggableSyntheticListeners,
  ) => React.ReactNode;
};

const Reorder = <T extends ReorderItemType = any>(props: ReorderProps) => {
  const {
    items: i,
    customRender,
    onReorder,
    customFilter = () => true,
    className,
    showDragger = true,
    disabled = false,
  } = props;
  const [items, setItems] = useState<{ id: string; data: T }[]>([]);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const allIds = items.map((item: { id: string; data: T }) => item.id);
      const oldIndex = allIds.indexOf(active.id);
      const newIndex = allIds.indexOf(over?.id || '');
      const newLocalItems = arrayMove(items, oldIndex, newIndex);
      const newItems = arrayMove(i, oldIndex, newIndex);
      setItems(newLocalItems);
      if (onReorder) onReorder([...newItems]);
    }
  };

  React.useEffect(() => {
    if (!i || !isArray(i)) return;
    setItems(i.map((item) => ({ id: nanoid(), data: item })));
  }, [i]);

  return (
    <ReorderContainer className={className || ''}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[
          restrictToVerticalAxis,
          restrictToWindowEdges,
          restrictToFirstScrollableAncestor,
        ]}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {items
            .filter((i) => customFilter(i.data))
            .map((item, index) => (
              <SortableItem
                customRender={customRender}
                item={item.data}
                disabled={disabled}
                itemIndex={index}
                showDragger={showDragger}
                key={item.id}
                id={item.id}
              />
            ))}
        </SortableContext>
      </DndContext>
    </ReorderContainer>
  );
};

const SortableItem = <T extends ReorderItemType = any>(props: {
  item: T;
  itemIndex: number;
  id: string;
  showDragger: boolean;
  disabled: boolean;
  customRender: (
    item: T,
    index: number,
    listeners?: DraggableSyntheticListeners,
  ) => React.ReactNode;
}) => {
  const { id, item, itemIndex, customRender, showDragger, disabled } = props;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <ItemContainer ref={setNodeRef} style={style} {...attributes} key={id}>
      {showDragger && (
        <div
          className={`dragger ${disabled ? 'disabled' : ''}`}
          {...(disabled ? {} : listeners)}
        >
          <Icon width="8" height="15" glyph="drag" />
        </div>
      )}
      {customRender(item, itemIndex, listeners)}
    </ItemContainer>
  );
};

export default Reorder;
